import React, { useEffect, useRef } from "react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  filterBy,
  CompositeFilterDescriptor,
} from "@progress/kendo-data-query";
import {
  Grid as KendoGrid,
  GridColumn as Column,
  GridFilterChangeEvent,
  GridPageChangeEvent,
  GridCustomHeaderCellProps,
  GridCustomCellProps,
  GridCellProps,
} from "@progress/kendo-react-grid";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { PagerTargetEvent } from "@progress/kendo-react-data-tools";
import { HeaderThElement } from "@progress/kendo-react-data-tools";
import { Typography } from "@progress/kendo-react-common";
import { LoadingPanel } from "../../components/layout/Loading";
import { getAllDashboardOpportunities } from "./services/dashboard.services";
import ButtonEdit from "../../components/drawer/ButtonEdit";
import { DELETE_ACCESS, EDIT_ACCESS } from "../../_contstants/common";
import { checkAcessRights } from "../../_helper/helper";
import IconButton from "../../components/common/IconButton";
import { openDialog } from "../../components/dialog/dialogSlice";
import { setOpportunityID } from "../opportunity/opportunitySlice";
import { MdDelete } from "react-icons/md";
import AppDrawer from "../../components/drawer/Drawer";
import CreateOpportunity from "../opportunity/CreateOpportunity";

const DashboardOpportunity: React.FC = () => {
  const dispatch = useAppDispatch();
  const isDrawerOpen = useAppSelector((state) => state.drawer.open);
  const drawerName = useAppSelector((state) => state.drawer.name);

  useEffect(() => {
    dispatch(getAllDashboardOpportunities());
  }, []);

  return (
    <>
      <GridLayout cols={[{ width: "100%" }]}>
        <GridLayoutItem
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "4px 20px 20px 0",
          }}
        >
          <Typography.h4 style={{ marginBottom: 0, marginLeft: "3px" }}>
            Opportunity List
          </Typography.h4>
        </GridLayoutItem>
        <GridLayoutItem>
          <DashboardInnerListGrid />
        </GridLayoutItem>
        {isDrawerOpen && drawerName === "opportunityForm" && (
          <AppDrawer>
            <CreateOpportunity />
          </AppDrawer>
        )}
      </GridLayout>
    </>
  );
};

interface PageState {
  skip: number;
  take: number;
}

const initialDataState: PageState = { skip: 0, take: 10 };

const DashboardInnerListGrid: React.FC = () => {
  const gridRef = useRef<any>(null);
  const dispatch = useAppDispatch();

  const DashboardOpportunityList = useAppSelector(
    (state) => state.dashboard?.DashboardOpportunityList
  );

  const loading = useAppSelector((state) => state.dashboard?.loading);
  const isEditAccess = checkAcessRights("/opportunity", EDIT_ACCESS);
  const isDeleteAccess = checkAcessRights("/opportunity", DELETE_ACCESS);

  const [filter, setFilter] = React.useState<
    CompositeFilterDescriptor | undefined
  >(undefined);
  const [page, setPage] = React.useState<PageState>(initialDataState);
  const [pageSizeValue, setPageSizeValue] = React.useState<
    number | string | undefined
  >();

  const pageChange = (event: GridPageChangeEvent) => {
    const targetEvent = event.targetEvent as PagerTargetEvent;
    const take =
      targetEvent.value === "All"
        ? DashboardOpportunityList.length
        : event.page.take;

    if (targetEvent.value) {
      setPageSizeValue(targetEvent.value);
    }
    setPage({
      ...event.page,
      take,
    });
  };

  const handleFilterChange = (e: GridFilterChangeEvent) => {
    setFilter(e.filter);
    setPage({ skip: 0, take: page.take });
  };

  const HeaderCustomCell = (props: GridCustomHeaderCellProps) => (
    <HeaderThElement
      columnId={props.thProps?.columnId || ""}
      style={props.thProps?.style}
      {...props.thProps}
      className={`table-header ${props.index === 5 ? "locked-header" : ""}`}
    >
      {props.children}
    </HeaderThElement>
  );

  const CustomCell = (props: any) => {
    return (
      <td
        style={{ ...props.style }}
        {...props.tdProps}
        colSpan={1}
        className={`table-cell ${props.className}`}
      >
        {props.children}
      </td>
    );
  };

  const MyDataCustomCell = (props: GridCustomCellProps) => (
    <CustomCell {...props} />
  );

  const MyEditCommandCell = (props: GridCellProps) => {
    const handleOpenDeleteDialog = (OpportunityGUID: any) => {
      dispatch(openDialog("deleteOpportunity"));
      dispatch(setOpportunityID(OpportunityGUID));
    };
    return (
      <td className={`action-td ${props.className}`} style={{ ...props.style }}>
        {isEditAccess && (
          <ButtonEdit
            ID={props.dataItem?.OpportunityGUID}
            drawerName="opportunityForm"
            title="Edit"
          />
        )}
        {isDeleteAccess && (
          <IconButton
            type="button"
            fillMode="flat"
            size="small"
            title="Delete"
themeColor="error"
            onClick={() =>
              handleOpenDeleteDialog(props.dataItem?.OpportunityGUID)
            }
          >
            <MdDelete
              className="absolute-position"
              style={{ fontSize: "24px" }}
            />
          </IconButton>
        )}
      </td>
    );
  };

  if (loading) return <LoadingPanel gridRef={gridRef} />;
  return (
    <>
      <KendoGrid
        style={{ maxHeight: "calc(100vh - 152px)" }}
        className="responsive-table"
        filterable={true}
        filter={filter}
        data={
          filter
            ? filterBy(DashboardOpportunityList, filter).slice(
                page.skip,
                page.take + page.skip
              )
            : DashboardOpportunityList.slice(page.skip, page.take + page.skip)
        }
        onFilterChange={handleFilterChange}
        skip={page.skip}
        take={page.take}
        total={DashboardOpportunityList.length}
        pageable={{
          buttonCount: 5,
          pageSizes: [5, 10, 15, "All"],
          pageSizeValue: pageSizeValue,
          type: "input",
        }}
        onPageChange={pageChange}
        cells={{
          headerCell: HeaderCustomCell,
          data: MyDataCustomCell,
        }}
      >
        <Column
          field="CustomerName"
          title="Customer Name "
          width={200}
          cell={(props: any) => (
            <td className="hoverableName">
              {isEditAccess ? (
                <ButtonEdit
                  ID={props.dataItem?.OpportunityGUID}
                  drawerName="opportunityForm"
                  label={props.dataItem?.CustomerName}
                />
              ) : (
                props.dataItem?.CustomerName
              )}
            </td>
          )}
        />
        <Column field="OpportunityType" title="Opportunity Type" width={200} />
        <Column field="SetPriority" title="Priority" width={200} />
        <Column field="FollowupDate" title="Followup Date" width={200} />
        <Column field="AssignTeam" title="Assign Team" width={200} />
        {(isDeleteAccess || isEditAccess) && (
          <Column
            field="OpportunityGUID"
            title="Actions"
            cell={MyEditCommandCell}
            width={"110px"}
            filterable={false}
            locked={true}
          />
        )}
      </KendoGrid>
    </>
  );
};

export default DashboardOpportunity;
